import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import {
    type AddClientContactInput,
    addClientContactInputSchema,
    type EmailInUseError,
    resolveEmailInUseError,
    useAddClientContactOptions,
} from '@/entities/client';
import { useSheetStore } from '@/shared/stores/sheet-store';

import { ADD_CLIENT_CONTACT_DEFAULTS } from '../config/add-client-contact-defaults.config';

export function useAddClientContactForm(clientId: string) {
    const goBack = useSheetStore((state) => state.goBack);

    const addContactMutation = useMutation(useAddClientContactOptions(clientId));

    const form = useForm<AddClientContactInput>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        resolver: zodResolver(addClientContactInputSchema, undefined, { raw: true }),
        defaultValues: ADD_CLIENT_CONTACT_DEFAULTS,
    });

    const [isAddressOpen, setIsAddressOpen] = useState(false);
    const [emailInUse, setEmailInUse] = useState<EmailInUseError | null>(null);

    const onSubmit = async (data: AddClientContactInput) => {
        const sheetAtSubmit = useSheetStore.getState().currentSheet;

        try {
            await addContactMutation.mutateAsync(data);

            // The therapist may have left mid-save and reopened the sheet, so a late resolve must
            // only pop the entry it was submitted from, never a newer one for the same client.
            if (useSheetStore.getState().currentSheet === sheetAtSubmit) {
                goBack();
            }
        } catch (error) {
            // Anything else is surfaced by the mutation's own toast; either way the form stays
            // mounted with whatever the therapist entered (FR-022, SC-005) since we neither reset
            // nor navigate away here.
            setEmailInUse(resolveEmailInUseError(error));
        }
    };

    return {
        form,
        submit: form.handleSubmit(onSubmit),
        isPending: addContactMutation.isPending,
        isAddressOpen,
        setIsAddressOpen,
        emailInUse,
        clearEmailInUse: () => setEmailInUse(null),
    };
}
